import { type HTMLAttributes, type ReactNode, useMemo, useState } from 'react';

import type { DataTableFilterField, DataTableFilterOption } from '@r/types/index.ts';
import { ArrowRight, DownloadIcon, ListX, Loader2, PlusIcon } from 'lucide-react';
import type { Table } from '@tanstack/react-table';

import { cn } from '@r/utils/utils.ts';
import { Button } from '@r/components/ui/button.tsx';
import { DataTableFilterCombobox } from '@r/components/data-table/advanced/data-table-filter-combobox.tsx';
import { DataTableViewOptions } from '@r/components/data-table/data-table-view-options.tsx';

import { DataTableFilterItem } from './data-table-filter-item.tsx';
import { useTranslation } from 'react-i18next';

interface DataTableAdvancedToolbarProps<TData> extends HTMLAttributes<HTMLDivElement> {
  table: Table<TData>;
  filterFields?: DataTableFilterField<TData>[];
  onExport?: () => void;
  isExporting?: boolean;
  canAdd?: boolean;
  onAdd?: () => void;
  title?: string;
  titleIcon?: ReactNode;
}

export function DataTableAdvancedToolbar<TData>({
  table,
  filterFields = [],
  children,
  className,
  onExport,
  onAdd,
  isExporting,
  canAdd,
  title,
  titleIcon,
  ...props
}: DataTableAdvancedToolbarProps<TData>) {
  const { t } = useTranslation();
  const isFiltered = table.getState().columnFilters.length > 0;

  const options = useMemo<DataTableFilterOption<TData>[]>(() => {
    return filterFields.map((field) => {
      return {
        id: crypto.randomUUID(),
        label: field.label,
        value: field.value,
        options: field.options ?? [],
        placeholder: field.placeholder,
        isDateRange: field.isDateRange
      };
    });
  }, [filterFields]);

  const initialSelectedOptions = useMemo(() => {
    return options;
  }, [options]);

  const [selectedOptions, setSelectedOptions] =
    useState<DataTableFilterOption<TData>[]>(initialSelectedOptions);
  const [openFilterBuilder, setOpenFilterBuilder] = useState(true);
  const [openCombobox, setOpenCombobox] = useState(false);

  function onFilterComboboxItemSelect() {
    setOpenFilterBuilder(true);
    setOpenCombobox(true);
  }

  return (
    <div
      className={cn('flex w-full flex-col space-y-2.5 overflow-auto py-2.5', className)}
      {...props}
    >
      <div className="flex flex-wrap gap-5 justify-between items-center">
        {title && (
          <div className="flex items-center text-xl font-bold gap-2.5">
            {titleIcon}
            <h2>{title}</h2>
          </div>
        )}

        <div className="flex gap-2 items-center ml-auto">
          {children}
          {isFiltered && (
            <Button
              rounded="full"
              variant="outline"
              className="h-7 rounded-full"
              onClick={() => table.resetColumnFilters()}
            >
              {/* Reset global: vide les filtres actifs et met a jour l'etat persiste. */}
              <ListX className="mr-2 size-4" aria-hidden="true" />
              {t('table.clear.filters')}
            </Button>
          )}
          <DataTableViewOptions table={table} />
          {canAdd && onAdd ? (
            <Button rounded="full" onClick={onAdd} className="h-8">
              <ArrowRight className="mr-2" size={15} />
              {t('btn.new.label')}
            </Button>
          ) : null}
          {onExport && (
            <Button rounded="full" onClick={onExport} disabled={isExporting} className="h-8">
              {isExporting ? (
                <Loader2 className="mr-2 animate-spin" size={15} />
              ) : (
                <DownloadIcon className="mr-2" size={15} />
              )}
              {t('btn.export.label')}
            </Button>
          )}
        </div>
      </div>

      <div className={cn('flex flex-wrap items-center gap-2', !openFilterBuilder && 'hidden')}>
        {selectedOptions
          .filter((option) => !option.isMulti)
          .map((selectedOption) => (
            <DataTableFilterItem
              key={String(selectedOption.value)}
              table={table}
              selectedOption={selectedOption}
              setSelectedOptions={setSelectedOptions}
              defaultOpen={openCombobox}
            />
          ))}
        {options.length > 0 && options.length > selectedOptions.length ? (
          <DataTableFilterCombobox
            options={options}
            selectedOptions={selectedOptions}
            setSelectedOptions={setSelectedOptions}
            onSelect={onFilterComboboxItemSelect}
          >
            <Button
              rounded="full"
              variant="outline"
              size="custom_sm"
              role="combobox"
              aria-expanded={openCombobox}
              name="Add filter"
              className="h-7 rounded-full"
              onClick={() => setOpenCombobox(true)}
            >
              <PlusIcon className="mr-2 opacity-50 size-4" aria-hidden="true" />
              Add filter
            </Button>
          </DataTableFilterCombobox>
        ) : null}
      </div>
    </div>
  );
}
